import React, { useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import reducer, {
  initialState,
  setError,
  setStores,
  setUser,
  selectStore,
} from "./reducer";
import { authenticate, getStores } from "../../services/storeEventApi";
import { Action } from "../../types/action";
import { getAuthToken, setAuthToken } from "../../services/storeEventApi/utils";
import { useHistory, useLocation } from "react-router-dom";

type ContextType = {
  dispatch: React.Dispatch<Action>;
  useSelector: Function;
};

const AppContext = React.createContext<ContextType>(undefined!);

export const useAppContext = () => useContext(AppContext);

const AppContextProvider = (props: { children: React.ReactNode }) => {
  const [headerStore, setHeaderStore] = useState<string | null>(null);
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const useSelector = (callback: Function) => callback(state);
  const history = useHistory();
  const { pathname } = useLocation();

  const getHeaderStore = async () => {
    const store = await window.ikea?.store?.get();
    setHeaderStore(`sto${store}`);
  };

  const fetchStores = async () => {
    const storelist = await getStores();
    dispatch(setStores(storelist));
  };

  const fetchUserInfo = async () => {
    if (getAuthToken()) {
      const userInfo = await authenticate();
      dispatch(setUser(userInfo));
    }
  };

  useEffect(() => {
    if (state.selectedEvent && !state.selectedStore) {
      const event = state.events[state.selectedEvent];

      dispatch(selectStore(event?.location?.store?.storeNo));
    }
  }, [state.selectedEvent, state.selectedStore, state.events]);

  useEffect(() => {
    if (pathname === "/" && headerStore && state.stores[headerStore]) {
      const store = state.stores[headerStore];
      history.push(`/${store.slug}/`);
    }
  }, [headerStore, state.stores, history, pathname]);

  useEffect(() => {
    const init = async () => {
      try {
        setAuthToken();
        await Promise.all([fetchUserInfo(), fetchStores(), getHeaderStore()]);
      } catch (e) {
        dispatch(setError());
        Sentry.captureException(e);
      }
    };
    init();
  }, []);

  useEffect(() => {
    window.ikea?.pubsub?.subscribe("NAVIGATION/STORE_SET", (store: string) =>
      setHeaderStore(`sto${store}`)
    );
  }, []);

  return (
    <AppContext.Provider value={{ dispatch, useSelector }}>
      {props.children}
    </AppContext.Provider>
  );
};

export * from "./reducer";
export * from "./selectors";
export default AppContextProvider;
